import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return toast.error("Captura tu correo");
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
            setDone(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "No se pudo enviar el correo");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-slate-100" data-testid="forgot-password-page">
            <div className="hidden lg:flex flex-1 bg-slate-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-red-900/20"></div>
                <div className="relative z-10 p-12 flex flex-col justify-between w-full">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center font-display font-black text-lg">F</div>
                            <div>
                                <div className="font-display font-black text-xl">FindMed</div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-400">Dispatch Control</div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold mb-3">RECUPERACIÓN DE ACCESO</div>
                        <h1 className="font-display font-black text-4xl tracking-tight mb-3">¿Olvidaste tu contraseña?</h1>
                        <p className="text-slate-400 text-sm max-w-md">
                            Te enviaremos un enlace seguro a tu correo registrado para que crees una nueva contraseña.
                            El enlace caduca a los 60 minutos.
                        </p>
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">© 2026 FindMed · Cumplimiento NOM-237-SSA1</div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                    <Link to="/login" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-6">
                        <ArrowLeft className="w-3 h-3" /> Volver al inicio de sesión
                    </Link>

                    {done ? (
                        <div className="fm-card p-6 border-l-4 border-l-emerald-500" data-testid="forgot-password-done">
                            <CheckCircle2 className="w-10 h-10 text-emerald-600 mb-3" strokeWidth={1.5} />
                            <h2 className="font-display font-bold text-2xl mb-2">Revisa tu correo</h2>
                            <p className="text-sm text-slate-600 leading-relaxed mb-3">
                                Si <strong>{email}</strong> es una cuenta válida en FindMed, en los próximos minutos
                                recibirás un correo con un enlace para restablecer tu contraseña.
                            </p>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                ¿No te llegó nada en 5 minutos? Revisa tu carpeta de Spam o contacta al administrador.
                                El enlace es válido por 60 minutos.
                            </p>
                            <Link to="/login" className="fm-btn-outline w-full justify-center mt-5">
                                Volver al inicio de sesión
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="fm-card p-6">
                            <div className="text-[10px] uppercase tracking-widest text-red-600 font-bold mb-2">RECUPERAR ACCESO</div>
                            <h2 className="font-display font-bold text-2xl mb-1">¿Olvidaste tu contraseña?</h2>
                            <p className="text-sm text-slate-500 mb-5">Captura tu correo y te enviaremos un enlace para crear una nueva.</p>

                            <label className="fm-label">Correo electrónico</label>
                            <div className="relative">
                                <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                                <input
                                    type="email"
                                    required
                                    autoFocus
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="fm-input pl-9"
                                    placeholder="tu@findmed.mx"
                                    data-testid="forgot-email-input"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="fm-btn-primary w-full justify-center mt-5"
                                data-testid="forgot-submit-btn"
                            >
                                {loading ? "Enviando..." : "Enviarme el enlace"}
                            </button>

                            <div className="text-[11px] text-slate-500 mt-4 text-center">
                                ¿Recordaste tu contraseña? <Link to="/login" className="text-red-600 hover:underline font-bold">Iniciar sesión</Link>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
